import { supabase } from '../lib/supabase';

export interface MasteryRecord {
  [questionId: string]: {
    correct_count: number;
    incorrect_count: number;
    last_incorrect_at: string | null;
    updated_at?: string | null;
    is_bookmarked?: boolean;
  };
}

export interface MasteryHistoryRecord {
  question_id: string;
  correct_count: number;
  incorrect_count: number;
  is_correct: boolean;
  updated_at: string;
}

async function getSessionUser() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.user) return null;
    return session.user;
  } catch {
    return null;
  }
}

/** Fetch mastery data from Supabase for the logged-in user */
export async function fetchMasteryFromDB(): Promise<MasteryRecord> {
  const user = await getSessionUser();
  if (!user) return {};

  const { data, error } = await supabase
    .from('quiz_mastery')
    .select('question_id, correct_count, incorrect_count, last_incorrect_at, updated_at, is_bookmarked')
    .eq('user_id', user.id);

  if (error) {
    console.error('Failed to fetch mastery:', error);
    return {};
  }

  const record: MasteryRecord = {};
  (data ?? []).forEach((row: {
    question_id: string;
    correct_count: number;
    incorrect_count: number;
    last_incorrect_at: string | null;
    updated_at: string | null;
    is_bookmarked: boolean | null;
  }) => {
    record[row.question_id] = {
      correct_count: row.correct_count,
      incorrect_count: row.incorrect_count || 0,
      last_incorrect_at: row.last_incorrect_at || null,
      updated_at: row.updated_at || null,
      is_bookmarked: row.is_bookmarked || false,
    };
  });
  return record;
}

/** Fetch mastery history data for time series analysis */
export async function fetchMasteryHistoryFromDB(): Promise<MasteryHistoryRecord[]> {
  const user = await getSessionUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('quiz_mastery')
    .select('question_id, correct_count, incorrect_count, updated_at')
    .eq('user_id', user.id)
    .order('updated_at', { ascending: true });

  if (error) {
    console.error('Failed to fetch mastery history:', error);
    return [];
  }

  return (data ?? []).map((row: {
    question_id: string;
    correct_count: number;
    incorrect_count: number;
    updated_at: string;
  }) => ({
    question_id: row.question_id,
    correct_count: row.correct_count,
    incorrect_count: row.incorrect_count,
    is_correct: row.correct_count > 0,
    updated_at: row.updated_at,
  }));
}

/** Record a correct answer - increment count */
export async function recordCorrectDB(questionId: string): Promise<void> {
  const user = await getSessionUser();
  if (!user) return;

  const { data: existing, error: fetchError } = await supabase
    .from('quiz_mastery')
    .select('id, correct_count, incorrect_count, last_incorrect_at')
    .eq('user_id', user.id)
    .eq('question_id', questionId)
    .maybeSingle();

  if (fetchError) {
    console.error('Failed to fetch existing mastery record:', fetchError);
    return;
  }

  if (existing) {
    // 重要：incorrect_count は絶対にリセットしない。
    // 一度でも間違えた記録（incorrect_count > 0）は永続保持する。
    // これにより「初見で間違えた」という事実が正確に記録され続ける。
    const { error: updateError } = await supabase
      .from('quiz_mastery')
      .update({
        correct_count: existing.correct_count + 1,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id);

    if (updateError) {
      console.error('Failed to update mastery record:', updateError);
    }
  } else {
    const { error: insertError } = await supabase
      .from('quiz_mastery')
      .insert({
        user_id: user.id,
        question_id: questionId,
        correct_count: 1,
        incorrect_count: 0,
      });

    if (insertError) {
      console.error('Failed to insert mastery record:', insertError);
    }
  }
}

/** Record an incorrect answer - DO NOT reset correct_count, only increment incorrect_count */
export async function recordIncorrectDB(questionId: string): Promise<void> {
  const user = await getSessionUser();
  if (!user) return;

  const { data: existing, error: fetchError } = await supabase
    .from('quiz_mastery')
    .select('id, correct_count, incorrect_count')
    .eq('user_id', user.id)
    .eq('question_id', questionId)
    .maybeSingle();

  if (fetchError) {
    console.error('Failed to fetch existing mastery record:', fetchError);
    return;
  }

  const now = new Date().toISOString();

  if (existing) {
    // 重要：correct_countはリセットしない。不正解カウントのみ増やす
    // これにより「学習中」状態が維持される
    const { error: updateError } = await supabase
      .from('quiz_mastery')
      .update({
        incorrect_count: (existing.incorrect_count || 0) + 1,
        last_incorrect_at: now,
        updated_at: now,
      })
      .eq('id', existing.id);

    if (updateError) {
      console.error('Failed to update mastery record:', updateError);
    }
  } else {
    // 初めて不正解の場合は新規レコード作成
    const { error: insertError } = await supabase
      .from('quiz_mastery')
      .insert({
        user_id: user.id,
        question_id: questionId,
        correct_count: 0,
        incorrect_count: 1,
        last_incorrect_at: now,
      });

    if (insertError) {
      console.error('Failed to insert mastery record:', insertError);
    }
  }
}

/** Get mastery level for display: 0=未学習, 1=学習中, 2+=習熟済 */
export function getMasteryLevel(record: MasteryRecord, questionId: string): number {
  return record[questionId]?.correct_count || 0;
}

/** Check if question has been answered incorrectly */
export function hasIncorrectHistory(record: MasteryRecord, questionId: string): boolean {
  const data = record[questionId];
  return data ? data.incorrect_count > 0 : false;
}

/** Get incorrect count for a question */
export function getIncorrectCount(record: MasteryRecord, questionId: string): number {
  return record[questionId]?.incorrect_count || 0;
}

/** Get last incorrect timestamp for a question */
export function getLastIncorrectAt(record: MasteryRecord, questionId: string): string | null {
  return record[questionId]?.last_incorrect_at || null;
}

/** Toggle bookmark status for a question */
export async function toggleBookmarkDB(questionId: string, currentStatus: boolean): Promise<boolean> {
  const user = await getSessionUser();
  if (!user) return currentStatus;

  const newStatus = !currentStatus;

  const { data: existing, error: fetchError } = await supabase
    .from('quiz_mastery')
    .select('id')
    .eq('user_id', user.id)
    .eq('question_id', questionId)
    .maybeSingle();

  if (fetchError) {
    console.error('Failed to fetch existing mastery record:', fetchError);
    return currentStatus;
  }

  if (existing) {
    const { error: updateError } = await supabase
      .from('quiz_mastery')
      .update({
        is_bookmarked: newStatus,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id);

    if (updateError) {
      console.error('Failed to update bookmark:', updateError);
      return currentStatus;
    }
  } else {
    const { error: insertError } = await supabase
      .from('quiz_mastery')
      .insert({
        user_id: user.id,
        question_id: questionId,
        correct_count: 0,
        incorrect_count: 0,
        is_bookmarked: newStatus,
      });

    if (insertError) {
      console.error('Failed to insert bookmark:', insertError);
      return currentStatus;
    }
  }

  return newStatus;
}

/** Check if question is bookmarked */
export function isBookmarked(record: MasteryRecord, questionId: string): boolean {
  return record[questionId]?.is_bookmarked || false;
}